import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { SharedModule, TreeDragDropService, TreeNode, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ContextMenuModule } from 'primeng/contextmenu';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { TreeNodeContextMenuSelectEvent, TreeNodeDropEvent } from 'primeng/types/tree';
import { lastValueFrom } from 'rxjs';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { MenuService } from '../../../../@core/services/menu.service';
import { Menu, Operator, Role } from '../../../../generated/generated-types';
import { RoleService } from '../../../../shared/services/role.service';
import {
  MenuItemClipboardData,
  MenuItemClipboardRoleRef,
  readMenuItemClipboard,
  remapRequiredRoleIds,
  writeMenuItemClipboard,
} from './menu-item-clipboard';
import { MenuItemInspectorComponent } from './menu-item-inspector.component';
import {
  EditableNavigationItem,
  createDefaultNavigationItem,
  isMenuTreeStructureValid,
  isNavigableLeafType,
  parseMenuItemsAsJson,
  serializeNavigationItems,
} from './menu-navigation-item.types';

type MenuTreeNode = TreeNode<EditableNavigationItem>;

function formatReferencedMenuLabel(menu: Pick<Menu, 'description' | 'identifier'>): string {
  const description = menu.description?.trim();
  if (description) {
    return description;
  }
  return menu.identifier?.trim() ?? '';
}

@Component({
  selector: 'app-menu-items-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TreeDragDropService, MenuService, RoleService],
  host: {
    class: 'flex min-h-0 flex-1 flex-col h-full w-full overflow-hidden',
  },
  imports: [
    CommonModule,
    FormsModule,
    FormlyModule,
    TranslatePipe,
    SharedModule,
    TreeModule,
    ButtonModule,
    TooltipModule,
    ContextMenuModule,
    MenuItemInspectorComponent,
  ],
  templateUrl: './menu-items-editor.component.html',
  styles: [`
    :host {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
      height: 100%;
      width: 100%;
      overflow: hidden;
    }

    ::ng-deep .p-tree-node-label {
      @apply w-full;
    }

    /* Constrain tree to leftover column height; .p-tree-root is the scrollport */
    ::ng-deep p-tree.menu-items-tree {
      display: flex !important;
      flex: 1 1 0% !important;
      flex-direction: column !important;
      min-height: 0 !important;
      height: auto !important;
      max-height: 100%;
      overflow: hidden !important;
    }

    ::ng-deep p-tree.menu-items-tree .p-tree,
    ::ng-deep p-tree.menu-items-tree.p-tree {
      min-height: 0 !important;
      height: 100%;
      overflow: hidden;
    }

    ::ng-deep p-tree.menu-items-tree .p-tree-root {
      flex: 1 1 0% !important;
      min-height: 0 !important;
      overflow: auto !important;
    }
  `],
})
export class MenuItemsEditorComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);
  private readonly menuService = inject(MenuService);
  private readonly roleService = inject(RoleService);

  treeNodes: MenuTreeNode[] = [];
  contextMenuItems: MenuItem[] = [];
  readonly selectedNode = signal<MenuTreeNode | null>(null);
  private readonly selectionRevision = signal(0);
  /** menuId → description ?? identifier for tree labels */
  private readonly referencedMenuLabels = signal<ReadonlyMap<string, string>>(new Map());
  /** roleId → identifier (or description) for indicator tooltips */
  private readonly roleLabels = signal<ReadonlyMap<string, string>>(new Map());
  readonly selectedItem = computed(() => {
    this.selectionRevision();
    return this.selectedNode()?.data ?? null;
  });

  readonly canAddChild = computed(() => this.selectedNode()?.data?.type === 'dropdown');

  readonly isNestedUnderDropdown = computed(() => {
    this.selectionRevision();
    const selected = this.selectedNode();
    if (!selected) {
      return false;
    }
    return this.findParentNode(this.treeNodes, selected)?.data?.type === 'dropdown';
  });

  private isSyncingFromControl = false;
  /** Suppresses PrimeNG unselect while rebuilding the tree from the form control. */
  private isReloadingTree = false;

  ngOnInit(): void {
    this.loadFromControlValue(this.formControl.value);
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (this.isSyncingFromControl) {
          return;
        }
        this.loadFromControlValue(value);
        this.cdRef.markForCheck();
      });
  }

  displayTitle(node: MenuTreeNode): string {
    if (node.data?.type === 'menu') {
      const menuId = node.data.menuId?.trim();
      if (!menuId) {
        return this.i18n.translate('menu-items-editor.menu-reference-unset');
      }
      const label = this.referencedMenuLabels().get(menuId)?.trim();
      return label || this.i18n.translate('menu-items-editor.menu-reference-unset');
    }

    const title = node.data?.title?.trim();
    if (!title) {
      return this.i18n.translate('menu-items-editor.untitled');
    }
    const translated = this.i18n.translate(title);
    return translated || title;
  }

  hasReferencedMenu(node: MenuTreeNode): boolean {
    const menuId = node.data?.menuId?.trim();
    if (!menuId) {
      return false;
    }
    return !!this.referencedMenuLabels().get(menuId)?.trim();
  }

  displayType(node: MenuTreeNode): string {
    const type = node.data?.type || 'simple';
    switch (type) {
      case 'dropdown':
        return this.i18n.translate('menu-items-editor.type-dropdown');
      case 'header':
        return this.i18n.translate('menu-items-editor.type-header');
      case 'menu':
        return this.i18n.translate('menu-items-editor.type-menu');
      case 'customEntity':
        return this.i18n.translate('menu-items-editor.type-custom-entity');
      case 'simple':
      default:
        return this.i18n.translate('menu-items-editor.type-simple');
    }
  }

  hasOwnPermissionRequirement(node: MenuTreeNode): boolean {
    return (node.data?.requiresPermission?.length ?? 0) > 0;
  }

  hasOwnRoleRequirement(node: MenuTreeNode): boolean {
    return (node.data?.requiredRoleIds?.length ?? 0) > 0;
  }

  hasParentPermissionRequirement(node: MenuTreeNode): boolean {
    const parent = this.findParentNode(this.treeNodes, node);
    return (parent?.data?.requiresPermission?.length ?? 0) > 0;
  }

  hasParentRoleRequirement(node: MenuTreeNode): boolean {
    const parent = this.findParentNode(this.treeNodes, node);
    return (parent?.data?.requiredRoleIds?.length ?? 0) > 0;
  }

  showPermissionIndicator(node: MenuTreeNode): boolean {
    return this.hasOwnPermissionRequirement(node) || this.hasParentPermissionRequirement(node);
  }

  showRoleIndicator(node: MenuTreeNode): boolean {
    return this.hasOwnRoleRequirement(node) || this.hasParentRoleRequirement(node);
  }

  /** True when the permission gate is only inherited from the parent (not set on this item). */
  isPermissionIndicatorMuted(node: MenuTreeNode): boolean {
    return !this.hasOwnPermissionRequirement(node) && this.hasParentPermissionRequirement(node);
  }

  /** True when the role gate is only inherited from the parent (not set on this item). */
  isRoleIndicatorMuted(node: MenuTreeNode): boolean {
    return !this.hasOwnRoleRequirement(node) && this.hasParentRoleRequirement(node);
  }

  permissionTooltip(node: MenuTreeNode): string {
    return this.effectivePermissions(node).join(', ');
  }

  roleTooltip(node: MenuTreeNode): string {
    const labels = this.roleLabels();
    return this.effectiveRoleIds(node)
      .map(id => labels.get(id) ?? id)
      .join(', ');
  }

  private effectivePermissions(node: MenuTreeNode): string[] {
    if (this.hasOwnPermissionRequirement(node)) {
      return [...(node.data?.requiresPermission ?? [])];
    }
    const parent = this.findParentNode(this.treeNodes, node);
    return [...(parent?.data?.requiresPermission ?? [])];
  }

  private effectiveRoleIds(node: MenuTreeNode): string[] {
    if (this.hasOwnRoleRequirement(node)) {
      return [...(node.data?.requiredRoleIds ?? [])];
    }
    const parent = this.findParentNode(this.treeNodes, node);
    return [...(parent?.data?.requiredRoleIds ?? [])];
  }

  onSelectionChange(selection: TreeNode | TreeNode[] | null | undefined): void {
    if (this.isReloadingTree) {
      return;
    }
    const node = Array.isArray(selection) ? (selection[0] ?? null) : (selection ?? null);
    this.selectedNode.set((node as MenuTreeNode | null) ?? null);
    this.selectionRevision.update(v => v + 1);
  }

  clearSelection(): void {
    if (this.isReloadingTree) {
      return;
    }
    this.selectedNode.set(null);
    this.selectionRevision.update(v => v + 1);
  }

  onNodeDrop(_event: TreeNodeDropEvent): void {
    if (!isMenuTreeStructureValid(this.nodesToItems(this.treeNodes))) {
      this.loadFromControlValue(this.formControl.value);
      this.cdRef.markForCheck();
      return;
    }
    this.syncDroppableFlags(this.treeNodes);
    this.pushTreeToFormControl();
  }

  addRootItem(): void {
    const item = createDefaultNavigationItem();
    const node = this.createTreeNode(item);
    this.treeNodes = [...this.treeNodes, node];
    this.selectedNode.set(node);
    this.selectionRevision.update(v => v + 1);
    this.pushTreeToFormControl();
  }

  addChildItem(): void {
    const parent = this.selectedNode();
    if (!parent) {
      return;
    }
    this.addChildToNode(parent);
  }

  deleteSelectedItem(): void {
    const current = this.selectedNode();
    if (!current) {
      return;
    }
    this.deleteNode(current);
  }

  async copySelectedItem(): Promise<void> {
    const current = this.selectedNode();
    if (current) {
      await this.copyNode(current);
      return;
    }
    const items = this.nodesToItems(this.treeNodes);
    if (!items.length) {
      return;
    }
    await this.writeClipboardWithRoles(items);
  }

  /** Toolbar paste: below selection when selected, otherwise append as root. */
  async pasteItem(): Promise<void> {
    const data = await readMenuItemClipboard();
    if (!data?.items.length) {
      return;
    }
    const items = await this.remapClipboardItems(data);

    const selected = this.selectedNode();
    if (selected) {
      await this.pasteRelativeTo(selected, 'below', items);
      return;
    }
    this.insertItemsAsRoot(items);
  }

  onNodeContextMenuSelect(event: TreeNodeContextMenuSelectEvent): void {
    const node = event.node as MenuTreeNode | undefined;
    if (!node) {
      return;
    }
    this.selectedNode.set(node);
    this.selectionRevision.update(v => v + 1);
    this.contextMenuItems = this.buildContextMenuItems(node);
    this.cdRef.markForCheck();
  }

  private buildContextMenuItems(node: MenuTreeNode): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: this.i18n.translate(_('menu-items-editor.add-above')),
        icon: 'fa fa-arrow-up',
        command: () => this.addSiblingRelativeTo(node, 'above'),
      },
      {
        label: this.i18n.translate(_('menu-items-editor.add-below')),
        icon: 'fa fa-arrow-down',
        command: () => this.addSiblingRelativeTo(node, 'below'),
      },
    ];

    if (node.data?.type === 'dropdown') {
      items.push({
        label: this.i18n.translate(_('menu-items-editor.add-child')),
        icon: 'fa fa-sitemap',
        command: () => this.addChildToNode(node),
      });
    }

    items.push(
      { separator: true },
      {
        label: this.i18n.translate(_('menu-items-editor.copy')),
        icon: 'fa fa-copy',
        command: () => {
          void this.copyNode(node);
        },
      },
      {
        label: this.i18n.translate(_('menu-items-editor.paste-above')),
        icon: 'fa fa-paste',
        command: () => {
          void this.pasteRelativeTo(node, 'above');
        },
      },
      {
        label: this.i18n.translate(_('menu-items-editor.paste-below')),
        icon: 'fa fa-paste',
        command: () => {
          void this.pasteRelativeTo(node, 'below');
        },
      },
      { separator: true },
      {
        label: this.i18n.translate(_('menu-items-editor.delete')),
        icon: 'fa fa-trash',
        command: () => this.deleteNode(node),
      },
    );

    return items;
  }

  private async copyNode(node: MenuTreeNode): Promise<void> {
    const item = this.nodesToItems([node])[0];
    if (!item) {
      return;
    }
    await this.writeClipboardWithRoles([item]);
  }

  private async writeClipboardWithRoles(items: EditableNavigationItem[]): Promise<void> {
    const roles = await this.buildRoleClipboardRefs(items);
    await writeMenuItemClipboard(items, roles);
    this.cdRef.markForCheck();
  }

  private async buildRoleClipboardRefs(
    items: EditableNavigationItem[],
  ): Promise<MenuItemClipboardRoleRef[]> {
    const roleIds = this.collectRequiredRoleIds(items);
    if (!roleIds.length) {
      return [];
    }

    try {
      const res = await lastValueFrom(
        this.roleService.getItems({
          searchFilter: {
            field: 'id',
            operator: Operator.in,
            value: JSON.stringify(roleIds),
            type: 'array',
          },
          take: roleIds.length,
        }).single$,
      );
      const refs: MenuItemClipboardRoleRef[] = [];
      const nextLabels = new Map(this.roleLabels());
      for (const role of (res.getRoles?.items ?? []) as Role[]) {
        const id = role.id?.trim();
        const identifier = role.identifier?.trim();
        if (!id || !identifier) {
          continue;
        }
        refs.push({ id, identifier });
        nextLabels.set(id, identifier);
      }
      this.roleLabels.set(nextLabels);
      return refs;
    } catch {
      return [];
    }
  }

  private async remapClipboardItems(data: MenuItemClipboardData): Promise<EditableNavigationItem[]> {
    if (!data.roles.length) {
      return data.items;
    }

    const sourceIdToLocalId = await this.resolveLocalRoleIdsByIdentifier(data.roles);
    return remapRequiredRoleIds(data.items, sourceIdToLocalId, true);
  }

  private async resolveLocalRoleIdsByIdentifier(
    refs: MenuItemClipboardRoleRef[],
  ): Promise<Map<string, string>> {
    const sourceIdToLocalId = new Map<string, string>();
    const identifiers = [...new Set(refs.map(ref => ref.identifier).filter(Boolean))];
    if (!identifiers.length) {
      return sourceIdToLocalId;
    }

    try {
      const res = await lastValueFrom(
        this.roleService.getItems({
          searchFilter: {
            field: 'identifier',
            operator: Operator.in,
            value: JSON.stringify(identifiers),
            type: 'array',
          },
          take: identifiers.length,
        }).single$,
      );
      const localByIdentifier = new Map<string, string>();
      const nextLabels = new Map(this.roleLabels());
      for (const role of (res.getRoles?.items ?? []) as Role[]) {
        const id = role.id?.trim();
        const identifier = role.identifier?.trim();
        if (!id || !identifier) {
          continue;
        }
        localByIdentifier.set(identifier, id);
        nextLabels.set(id, identifier);
      }
      this.roleLabels.set(nextLabels);

      for (const ref of refs) {
        const localId = localByIdentifier.get(ref.identifier);
        if (localId) {
          sourceIdToLocalId.set(ref.id, localId);
        }
      }
    } catch {
      // Leave map empty — roles without a local match are dropped
    }

    return sourceIdToLocalId;
  }

  private async pasteRelativeTo(
    target: MenuTreeNode,
    position: 'above' | 'below',
    clipboardItems?: EditableNavigationItem[],
  ): Promise<void> {
    let items = clipboardItems;
    if (!items) {
      const data = await readMenuItemClipboard();
      if (!data?.items.length) {
        return;
      }
      items = await this.remapClipboardItems(data);
    }
    if (!items.length) {
      return;
    }

    const parent = this.findParentNode(this.treeNodes, target);
    const prepared = items
      .map(item => this.prepareItemForPaste(item, parent))
      .filter((item): item is EditableNavigationItem => !!item);
    if (!prepared.length) {
      return;
    }

    this.insertSiblingsRelativeTo(target, position, prepared);
  }

  private prepareItemForPaste(
    item: EditableNavigationItem,
    parent: MenuTreeNode | null,
  ): EditableNavigationItem | null {
    const [cloned] = serializeNavigationItems([item]);
    if (!cloned) {
      return null;
    }

    if (cloned.type === 'menu') {
      const excludeId = this.currentMenuId;
      if (excludeId && cloned.menuId === excludeId) {
        cloned.menuId = undefined;
      }
    }

    if (parent?.data?.type === 'dropdown') {
      if (!isNavigableLeafType(cloned.type)) {
        return null;
      }
      cloned.children = [];
      return cloned;
    }

    return cloned;
  }

  private insertItemsAsRoot(items: EditableNavigationItem[]): void {
    const prepared = items
      .map(item => this.prepareItemForPaste(item, null))
      .filter((item): item is EditableNavigationItem => !!item);
    if (!prepared.length) {
      return;
    }
    const nodes = prepared.map(item => this.createTreeNode(item));
    const nextTree = [...this.treeNodes, ...nodes];
    if (!isMenuTreeStructureValid(this.nodesToItems(nextTree))) {
      return;
    }
    this.treeNodes = nextTree;
    this.selectedNode.set(nodes[nodes.length - 1] ?? null);
    this.selectionRevision.update(v => v + 1);
    this.pushTreeToFormControl();
    void this.resolveReferencedMenuLabels(this.nodesToItems(this.treeNodes));
    void this.resolveRoleLabels(this.nodesToItems(this.treeNodes));
  }

  private insertSiblingsRelativeTo(
    target: MenuTreeNode,
    position: 'above' | 'below',
    items: EditableNavigationItem[],
  ): void {
    const parent = this.findParentNode(this.treeNodes, target);
    const siblings = parent
      ? ([...(parent.children as MenuTreeNode[] | undefined) ?? []] as MenuTreeNode[])
      : [...this.treeNodes];
    const index = siblings.findIndex(n => n.key === target.key);
    if (index < 0) {
      return;
    }

    const nodes = items.map(item => this.createTreeNode(item));
    const insertAt = position === 'above' ? index : index + 1;
    siblings.splice(insertAt, 0, ...nodes);

    let nextTree: MenuTreeNode[];
    if (parent) {
      parent.children = siblings;
      parent.expanded = true;
      if (parent.data) {
        parent.data.children = this.nodesToItems(siblings);
      }
      nextTree = [...this.treeNodes];
    } else {
      nextTree = siblings;
    }

    if (!isMenuTreeStructureValid(this.nodesToItems(nextTree))) {
      this.loadFromControlValue(this.formControl.value);
      this.cdRef.markForCheck();
      return;
    }

    this.treeNodes = nextTree;
    this.selectedNode.set(nodes[nodes.length - 1] ?? null);
    this.selectionRevision.update(v => v + 1);
    this.pushTreeToFormControl();
    void this.resolveReferencedMenuLabels(this.nodesToItems(this.treeNodes));
    void this.resolveRoleLabels(this.nodesToItems(this.treeNodes));
  }

  private addSiblingRelativeTo(target: MenuTreeNode, position: 'above' | 'below'): void {
    const parent = this.findParentNode(this.treeNodes, target);
    const item = createDefaultNavigationItem();
    if (parent?.data?.type === 'dropdown') {
      item.type = 'simple';
    }
    this.insertSiblingsRelativeTo(target, position, [item]);
  }

  private addChildToNode(parent: MenuTreeNode): void {
    if (parent.data?.type !== 'dropdown') {
      return;
    }
    const item = createDefaultNavigationItem();
    item.type = 'simple';
    const node = this.createTreeNode(item);
    parent.children = [...(parent.children ?? []), node];
    parent.expanded = true;
    if (parent.data) {
      parent.data.children = this.nodesToItems(parent.children as MenuTreeNode[]);
    }
    this.treeNodes = [...this.treeNodes];
    this.selectedNode.set(node);
    this.selectionRevision.update(v => v + 1);
    this.pushTreeToFormControl();
  }

  private deleteNode(node: MenuTreeNode): void {
    this.treeNodes = this.removeNodeByKey(this.treeNodes, node.key);
    if (this.selectedNode()?.key === node.key) {
      this.clearSelection();
    }
    this.pushTreeToFormControl();
  }

  onSelectedItemChange(updated: EditableNavigationItem): void {
    const current = this.selectedNode();
    if (!current) {
      return;
    }

    const previousType = current.data?.type || 'simple';
    const nextType = updated.type || 'simple';

    if (this.isNestedUnderDropdown() && !isNavigableLeafType(nextType)) {
      return;
    }

    let children = current.data?.children ?? updated.children ?? [];
    if (nextType !== 'dropdown') {
      children = [];
      current.children = undefined;
    } else if (previousType !== 'dropdown') {
      children = [];
      current.children = undefined;
    }

    const keepsIcon =
      nextType === 'dropdown' ||
      (isNavigableLeafType(nextType) && !this.isNestedUnderDropdown());

    current.data = {
      ...updated,
      children,
      title: nextType === 'menu' ? undefined : updated.title,
      link:
        nextType === 'simple' || nextType === 'customEntity' ? updated.link : undefined,
      icon: keepsIcon ? updated.icon : undefined,
      allowFavorite: isNavigableLeafType(nextType) ? updated.allowFavorite : false,
      menuId: nextType === 'menu' ? updated.menuId : undefined,
      customEntityIdentifier:
        nextType === 'customEntity' ? updated.customEntityIdentifier : undefined,
      badge: nextType === 'menu' || nextType === 'header' ? undefined : updated.badge,
    };
    current.label = this.displayTitle(current);
    current.droppable = nextType === 'dropdown';
    this.selectionRevision.update(v => v + 1);
    this.treeNodes = [...this.treeNodes];
    this.pushTreeToFormControl();
    void this.resolveReferencedMenuLabels(this.nodesToItems(this.treeNodes));
    void this.resolveRoleLabels(this.nodesToItems(this.treeNodes));
  }

  onReferencedMenuResolved(menu: Menu | null): void {
    if (!menu?.id) {
      return;
    }
    const label = formatReferencedMenuLabel(menu);
    if (!label) {
      return;
    }
    const next = new Map(this.referencedMenuLabels());
    next.set(menu.id, label);
    this.referencedMenuLabels.set(next);
    this.cdRef.markForCheck();
  }

  /** UUID of the menu entity being edited (Formly model), if available. */
  get currentMenuId(): string | null {
    const id = (this.model as { id?: string } | undefined)?.id;
    return typeof id === 'string' && id ? id : null;
  }

  private loadFromControlValue(value: unknown): void {
    const items = parseMenuItemsAsJson(value);
    const nextSerialized = JSON.stringify(serializeNavigationItems(items));
    const currentSerialized = JSON.stringify(
      serializeNavigationItems(this.nodesToItems(this.treeNodes)),
    );
    // Parent form often re-patches the same JSON after load/save; rebuilding would
    // replace node object identity (and used to regenerate keys) and clear selection.
    if (nextSerialized === currentSerialized) {
      void this.resolveReferencedMenuLabels(items);
      void this.resolveRoleLabels(items);
      return;
    }

    const selectedPath = this.getNodePath(this.treeNodes, this.selectedNode());
    this.isReloadingTree = true;
    this.treeNodes = this.itemsToNodes(items);
    this.selectedNode.set(
      selectedPath ? this.findNodeByPath(this.treeNodes, selectedPath) : null,
    );
    this.selectionRevision.update(v => v + 1);
    queueMicrotask(() => {
      this.isReloadingTree = false;
    });
    void this.resolveReferencedMenuLabels(items);
    void this.resolveRoleLabels(items);
  }

  private async resolveReferencedMenuLabels(items: EditableNavigationItem[]): Promise<void> {
    const menuIds = this.collectReferencedMenuIds(items);
    const missing = menuIds.filter(id => !this.referencedMenuLabels().has(id));
    if (!missing.length) {
      return;
    }

    try {
      const res = await lastValueFrom(
        this.menuService.getItems({
          searchFilter: {
            field: 'id',
            operator: Operator.in,
            value: JSON.stringify(missing),
            type: 'array',
          },
          take: missing.length,
        }).single$,
      );
      const next = new Map(this.referencedMenuLabels());
      for (const menu of (res.getMenus?.items ?? []) as Menu[]) {
        if (!menu.id) {
          continue;
        }
        const label = formatReferencedMenuLabel(menu);
        if (label) {
          next.set(menu.id, label);
        }
      }
      this.referencedMenuLabels.set(next);
      this.cdRef.markForCheck();
    } catch {
      // Labels stay on placeholder until a later refresh
    }
  }

  private collectReferencedMenuIds(items: EditableNavigationItem[]): string[] {
    const ids = new Set<string>();
    const visit = (list: EditableNavigationItem[]): void => {
      for (const item of list) {
        if (item.type === 'menu') {
          const menuId = item.menuId?.trim();
          if (menuId) {
            ids.add(menuId);
          }
        }
        if (item.children?.length) {
          visit(item.children);
        }
      }
    };
    visit(items);
    return [...ids];
  }

  private async resolveRoleLabels(items: EditableNavigationItem[]): Promise<void> {
    const roleIds = this.collectRequiredRoleIds(items);
    const missing = roleIds.filter(id => !this.roleLabels().has(id));
    if (!missing.length) {
      return;
    }

    try {
      const res = await lastValueFrom(
        this.roleService.getItems({
          searchFilter: {
            field: 'id',
            operator: Operator.in,
            value: JSON.stringify(missing),
            type: 'array',
          },
          take: missing.length,
        }).single$,
      );
      const next = new Map(this.roleLabels());
      for (const role of (res.getRoles?.items ?? []) as Role[]) {
        if (!role.id) {
          continue;
        }
        const label = role.identifier?.trim() || role.description?.trim() || role.id;
        next.set(role.id, label);
      }
      this.roleLabels.set(next);
      this.cdRef.markForCheck();
    } catch {
      // Tooltips fall back to role IDs until a later refresh
    }
  }

  private collectRequiredRoleIds(items: EditableNavigationItem[]): string[] {
    const ids = new Set<string>();
    const visit = (list: EditableNavigationItem[]): void => {
      for (const item of list) {
        for (const roleId of item.requiredRoleIds ?? []) {
          const trimmed = roleId.trim();
          if (trimmed) {
            ids.add(trimmed);
          }
        }
        if (item.children?.length) {
          visit(item.children);
        }
      }
    };
    visit(items);
    return [...ids];
  }

  private pushTreeToFormControl(): void {
    const items = serializeNavigationItems(this.nodesToItems(this.treeNodes));
    const currentSerialized = serializeNavigationItems(parseMenuItemsAsJson(this.formControl.value));
    if (JSON.stringify(items) === JSON.stringify(currentSerialized)) {
      return;
    }
    this.isSyncingFromControl = true;
    this.formControl.setValue(items);
    this.formControl.markAsDirty();
    this.isSyncingFromControl = false;
    this.cdRef.markForCheck();
  }

  private itemsToNodes(items: EditableNavigationItem[]): MenuTreeNode[] {
    return items.map((item, index) => this.createTreeNode(item, String(index)));
  }

  private createTreeNode(item: EditableNavigationItem, key?: string): MenuTreeNode {
    const nodeKey = key ?? crypto.randomUUID();
    const type = item.type || 'simple';
    const childItems = type === 'dropdown' ? (item.children ?? []) : [];
    const children = childItems.map((child, index) => {
      const childType = child.type === 'customEntity' ? 'customEntity' : 'simple';
      return this.createTreeNode({ ...child, type: childType }, `${nodeKey}.${index}`);
    });
    return {
      key: nodeKey,
      label: item.title ?? '',
      data: { ...item, type, children: childItems, ...(type === 'menu' ? { title: undefined } : {}) },
      children: children.length ? children : undefined,
      expanded: false,
      draggable: true,
      droppable: type === 'dropdown',
    };
  }

  private syncDroppableFlags(nodes: MenuTreeNode[]): void {
    for (const node of nodes) {
      node.droppable = (node.data?.type || 'simple') === 'dropdown';
      if (node.children?.length) {
        this.syncDroppableFlags(node.children as MenuTreeNode[]);
      }
    }
  }

  private nodesToItems(nodes: MenuTreeNode[] | undefined): EditableNavigationItem[] {
    return (nodes ?? []).map(node => {
      const data = { ...(node.data ?? createDefaultNavigationItem()) };
      const children = this.nodesToItems(node.children as MenuTreeNode[] | undefined);
      data.children = children;
      return data;
    });
  }

  private removeNodeByKey(nodes: MenuTreeNode[], key: string | undefined): MenuTreeNode[] {
    if (!key) {
      return nodes;
    }
    const result: MenuTreeNode[] = [];
    for (const node of nodes) {
      if (node.key === key) {
        continue;
      }
      const next: MenuTreeNode = {
        ...node,
        children: node.children
          ? this.removeNodeByKey(node.children as MenuTreeNode[], key)
          : undefined,
      };
      if (next.data) {
        next.data = {
          ...next.data,
          children: this.nodesToItems(next.children as MenuTreeNode[] | undefined),
        };
      }
      result.push(next);
    }
    return result;
  }

  /** Index path from root → node (stable across rebuilds when tree shape is unchanged). */
  private getNodePath(nodes: MenuTreeNode[], target: MenuTreeNode | null): number[] | null {
    if (!target?.key) {
      return null;
    }
    for (let index = 0; index < nodes.length; index++) {
      const node = nodes[index];
      if (node.key === target.key) {
        return [index];
      }
      const nested = this.getNodePath(
        (node.children as MenuTreeNode[] | undefined) ?? [],
        target,
      );
      if (nested) {
        return [index, ...nested];
      }
    }
    return null;
  }

  private findNodeByPath(nodes: MenuTreeNode[], path: number[]): MenuTreeNode | null {
    let currentNodes = nodes;
    let node: MenuTreeNode | null = null;
    for (const index of path) {
      node = currentNodes[index] ?? null;
      if (!node) {
        return null;
      }
      currentNodes = (node.children as MenuTreeNode[] | undefined) ?? [];
    }
    return node;
  }

  private findParentNode(nodes: MenuTreeNode[], target: MenuTreeNode): MenuTreeNode | null {
    for (const node of nodes) {
      const children = (node.children as MenuTreeNode[] | undefined) ?? [];
      if (children.some(child => child.key === target.key)) {
        return node;
      }
      const nested = this.findParentNode(children, target);
      if (nested) {
        return nested;
      }
    }
    return null;
  }
}
